import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  Dialog,
  TextField,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import {
  useCreateCoveragePolicyTermsContionMutation,
  useGetAllPolicyTermsQuery,
} from "../../../Api/attoDeskApi";
import {
  IPolicy,
  IPolicyTermSummary,
  ITermsCondition,
} from "../../../Api/Interface/api.interface";
import { useFormik } from "formik";
import { useStyles } from "../../../theme/appStyles";
import CloseIcon from "@mui/icons-material/Close";
import { useNotifier } from "../../../Core/Notifier";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Switch, { SwitchProps } from "@mui/material/Switch";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

interface LoopingTableProps {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data: IPolicy;
}

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 50,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: theme.palette.primary,
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "4px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 24,
    height: 24,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#757575" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 12,
      height: 12,
    },
    "&::before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 8,
    },
    "&::after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="12" width="12" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 8,
    },
  },
}));

const LoopingTable: React.FC<LoopingTableProps> = ({
  openModel = false,
  handleCloseDialog,
  data,
}) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showErrorMessage, showMessage } = useNotifier();
  const [policyTermsData, { isLoading: loopingDataLoading }] =
    useCreateCoveragePolicyTermsContionMutation();

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const {
    data: termsData,
    isLoading,
    isError,
  } = useGetAllPolicyTermsQuery(
    data.policyProductId
      ? {
          searchText: "",
          id: "",
          policyTermProductId: data.policyProductId,
        }
      : { searchText: "", id: "", policyTermProductId: "" },
    { skip: !data.policyProductId }
  );

  const termsAndConditionList = useMemo(() => {
    if (!termsData || !termsData.data) return [];

    return (termsData.data as IPolicyTermSummary[]).map((term) => ({
      id: term.id,
      policyTermProductId: term.policyTermProductId,
      policyTermsArray: term.policyTermsArray || [],
    }));
  }, [termsData]);

  const formik = useFormik<ITermsCondition>({
    initialValues: {
      id: data.id,
      policyTermsConditonsArray: data.policyTermsConditonsArray || [],
      policyAllTermsConditons: [],
    },

    onSubmit: async (values, { resetForm }) => {
      try {
        const temData = {
          id: values.id,
          policyTermsConditonsArray: values.policyTermsConditonsArray,
          policyAllTermsConditons: values.policyAllTermsConditons,
        };

        const addCompanyResponse = await policyTermsData(temData).unwrap();
        if (!addCompanyResponse.status) {
          showErrorMessage(addCompanyResponse.message);
        } else {
          showMessage(addCompanyResponse.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handlePolicyTermsToggle = (printerId: number) => {
    const { policyTermsConditonsArray } = formik.values;

    // Toggle the selected policy term ID
    const updatedPrinterIds = policyTermsConditonsArray.includes(printerId)
      ? policyTermsConditonsArray.filter((id) => id !== printerId) // Remove the ID
      : [...policyTermsConditonsArray, printerId]; // Add the ID

    formik.setFieldValue("policyTermsConditonsArray", updatedPrinterIds);
  };

  // useEffect(() => {
  //   const autoCheckedIds = termsAndConditionList
  //     .flatMap((term) =>
  //       term.policyTermsArray.filter(
  //         (printer) =>
  //           printer.isPolicyTermsSummaryReport || printer.isPolicyTermsAll
  //       )
  //     )
  //     .map((printer) => printer.id ?? 0);

  //   formik.setFieldValue(
  //     "policyTermsConditonsArray",
  //     Array.from(
  //       new Set([...formik.values.policyTermsConditonsArray, ...autoCheckedIds])
  //     )
  //   );
  // }, [termsAndConditionList]);

  useEffect(() => {
    const allIds = termsAndConditionList
      .flatMap((term) => term.policyTermsArray.map((item) => item.id))
      .filter((id) => id !== undefined);

    formik.setFieldValue("policyAllTermsConditons", allIds);
  }, [termsAndConditionList]);

  useEffect(() => {
    if (data.policyTermsConditonsArray) return;

    const autoCheckedIds = termsAndConditionList
      .flatMap((term) =>
        term.policyTermsArray.filter(
          (printer) =>
            printer.isPolicyTermsSummaryReport || printer.isPolicyTermsAll
        )
      )
      .map((printer) => printer.id ?? 0);

    formik.setFieldValue(
      "policyTermsConditonsArray",
      Array.from(
        new Set([...formik.values.policyTermsConditonsArray, ...autoCheckedIds])
      )
    );
  }, [termsAndConditionList, data]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-describedby="alert-dialog-description"
        classes={{ paper: "inherit" }}
        fullScreen={isMobile}
        fullWidth={isMobile}
        maxWidth="lg"
      >
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.card}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  pt={2}
                  alignContent={"center"}
                >
                  <Typography className={classes.cardTitle}>
                    Comments
                  </Typography>
                </Grid>
              </Grid>
              {termsAndConditionList.length === 0 ||
              termsAndConditionList.every(
                (term) =>
                  !term.policyTermsArray || term.policyTermsArray.length === 0
              ) ? (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ padding: 3, textAlign: "center" }}
                >
                  No terms available.
                </Typography>
              ) : (
                termsAndConditionList.map((term) =>
                  term.policyTermsArray.map((printer) => (
                    <Grid
                      container
                      spacing={2}
                      p={"24px"}
                      key={printer.id ?? term.id}
                    >
                      <Grid item lg={10} md={10} sm={12} xs={12}>
                        {/* <TextField
                          placeholder="Enter Terms"
                          size="small"
                          value={printer.policyTermsDescription}
                          multiline
                          rows={3}
                          className={classes.textField}
                          InputProps={{
                            readOnly: true,
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        /> */}
                        <ReactQuill
                          value={printer.policyTermsDescription}
                          theme="snow"
                          style={{ height: "150px" }} 
                          readOnly={true}
                          modules={{
                            toolbar: [
                              [{ font: [] }],
                              [{ header: [1, 2, 3, 4, 5, 6, false] }],
                              ["bold", "italic", "underline", "strike"],
                              [{ color: [] }, { background: [] }],
                              [{ script: "sub" }, { script: "super" }],
                              [{ list: "ordered" }, { list: "bullet" }],
                              [{ indent: "-1" }, { indent: "+1" }],
                              [{ direction: "rtl" }],
                              [{ align: [] }],
                              ["blockquote", "code-block"],
                              ["link", "image", "video"],
                              ["clean"],
                            ],
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        lg={2}
                        md={2}
                        sm={2}
                        xs={2}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <IOSSwitch
                          color="primary"
                          sx={{ mr: 2 }}
                          checked={formik.values.policyTermsConditonsArray.includes(
                            printer.id ?? 0
                          )}
                          onChange={() =>
                            handlePolicyTermsToggle(printer.id ?? 0)
                          }
                        />
                      </Grid>
                    </Grid>
                  ))
                )
              )}
            </Card>
          </Grid>

          <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
            <Card className={classes.card}>
              <Grid container spacing={3} p={"24px"}>
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  sx={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    className={classes.addButton}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Box m={0.5}></Box>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleOutlineIcon />}
                    className={classes.addButton}
                    onClick={() => formik.handleSubmit()}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Dialog>
    </div>
  );
};

export default LoopingTable;
